import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { wz } from '../../components/wizard-tokens';
import { MiSpinner, WizGradientButton, WizInput } from '../../components/wizard-ui';
import { isValidAdUrl } from './job-logic';

interface Props {
  open: boolean;
  /** Im Anzeigenfeld steht schon Text: Hinweis, dass er ersetzt wird. */
  hasText: boolean;
  reading: boolean;
  /** Fehler des letzten Einlesens (das Modal bleibt dann offen). */
  error: string | null;
  onClose: () => void;
  onSubmit: (url: string) => void;
}

/** Modal „Stellenanzeige per Link einlesen“ (Stil wie das Lücken-Popup). */
const LinkDialog: React.FC<Props> = ({ open, hasText, reading, error, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [url, setUrl] = useState('');
  const [touched, setTouched] = useState(false);
  const valid = isValidAdUrl(url);
  const content = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setUrl('');
      setTouched(false);
    }
  }, [open]);

  const submit = () => {
    setTouched(true);
    if (valid && !reading) onSubmit(url.trim());
  };

  return (
    <Dialog
      open={open}
      onClose={reading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: '16px' } }}
      TransitionProps={{ onEntered: () => content.current?.querySelector('input')?.focus() }}
    >
      <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>{t('wizLinkTitle')}</DialogTitle>
      <DialogContent
        ref={content}
        sx={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px !important' }}
      >
        <div style={{ fontSize: 15, color: wz.muted }}>{t('wizLinkHint')}</div>
        <WizInput
          value={url}
          onChange={setUrl}
          disabled={reading}
          invalid={touched && !valid}
          placeholder="https://"
          aria-label={t('wizLinkTitle')}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
        />
        {touched && !valid ? <div className={classes.error}>{t('wizLinkInvalid')}</div> : null}
        {error ? <div className={classes.error}>{error}</div> : null}
        {hasText ? <div className={classes.replace}>{t('wizLinkReplaces')}</div> : null}
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 20px', gap: '12px' }}>
        <button type="button" className={classes.cancel} onClick={onClose} disabled={reading}>
          {t('wizLinkCancel')}
        </button>
        <WizGradientButton
          onClick={submit}
          disabled={reading || url.trim() === ''}
          icon={reading ? <MiSpinner size={16} color="#FFF" /> : <LinkIcon sx={{ fontSize: 18 }} />}
        >
          {reading ? t('wizJobReadingUrl') : t('wizLinkSubmit')}
        </WizGradientButton>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()({
  error: { fontSize: 13, color: wz.redFg },
  replace: { fontSize: 13, color: wz.muted },
  cancel: {
    height: 40,
    padding: '0 20px',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    color: wz.primary,
    background: '#FFF',
    border: `1.5px solid ${wz.primary}`,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': { background: wz.primarySoft },
    '&:disabled': { opacity: 0.5, cursor: 'default' },
  },
});

export default LinkDialog;
